// side effect imports to run first
import './why-did-you-render';
import './polyfill';
import 'utils/accessibility/text-size';
import React, { useEffect, useRef, useState } from 'react';

import { Asset } from 'expo-asset';
import { useAppShortcuts } from 'features/app-shortcuts/use-app-shortcuts';
import { DiagnosticContextGlobalContributors } from 'features/diagnostics/contributors/global-contributors';
import { PreloadMinimalFavorites } from 'features/menu/pages/favorites-page/minimal-favorites/preload-minimal-favorites';
import { ActivityIndicator, Platform, StyleSheet, View } from 'react-native';

import { EnvPicker } from 'components/env-picker';
import { DefaultHelmetHTML } from 'components/layout/helmet-html.native';
import { GenericMiscellaneous } from 'components/miscellaneous/generic-miscellaneous';
import { PlatformSpecificMiscellaneous } from 'components/miscellaneous/platform-specific-miscellaneous';
import { Wrapper } from 'components/miscellaneous/wrapper';
import ReduceProviders from 'components/reduce-providers';
import { useApplyEnvRegionFromUrl } from 'hooks/environment/use-apply-env-region-from-url';
import { useOtaLogger } from 'hooks/use-ota-logger';
import { GlobalAppModals, RootOnlyGlobalAppModals } from 'navigation/global-app-modals';
import { DialogStaticPage } from 'pages/authentication/components/dialog-static-page';
import {
  TEMP_migrateAuthKeys,
  initializeSecureAuthStorage,
  isSecureAuthStorageReady,
} from 'remote/auth/secure-auth-storage';
import { initializeAmplitude } from 'state/amplitude/initialize';
import { useDeeplinks } from 'state/branch/hooks/use-deeplinks';
import { initializeExperiments } from 'state/experiments';
import { store } from 'state/global-state';
import { syncWithStorage } from 'state/global-state/global-actions';
import { Header, headers, setHeaders } from 'state/graphql/headers';
import { initLaunchDarklyPromise } from 'state/launchdarkly/launchdarkly-core';
import { primitive } from 'styles/constants';
import { GlobalStylesheet } from 'styles/global';
import { markEasObserveInteractiveOnce } from 'utils/eas-observe';
import { shouldShowEnvPickerAtStart } from 'utils/environment';
import { TEMP_asyncToSyncStorageMigration } from 'utils/environment/sync-storage';
import { initExpoFonts } from 'utils/expo';
import { handleFatalError } from 'utils/fatal-error-handler';
import { animatedSplashScreenHiddenDeferred } from 'utils/hide-splash-screen';
import logger from 'utils/logger';

import { fonts } from './styles/constants/fonts';

// Added this to prevent crash on Android
// See https://github.com/andyearnshaw/Intl.js#regexp-cache--restore
// and https://github.com/expo/expo/issues/6536
if (Platform.OS === 'android') {
  if (typeof (Intl as any).__disableRegExpRestore === 'function') {
    (Intl as any).__disableRegExpRestore();
  }
}

// block app start for up to TIMEOUT_MS to avoid race conditions, odd behavior on critical libraries
const TIMEOUT_MS = 3000;
const LONG_LOGGER_MS = 30000;
async function optionalAppStartPromise<T>(promise: Promise<T>, name: string): Promise<void> {
  const start = Date.now();

  let timeoutTimer: ReturnType<typeof setTimeout>;
  const loggingTimeoutPromise = new Promise<void>(resolve => {
    timeoutTimer = setTimeout(() => {
      const duration = Date.now() - start;
      logger.info({
        message: `${name} init timeout of ${TIMEOUT_MS}ms exceeded, app will start`,
        duration,
        isOptionalAppStartLog: true,
      });
      resolve();
    }, TIMEOUT_MS);
  });

  // LONG_LOGGER_MS is a long time, its probably failed at this point lets log it to see how often this happens...
  // TODO maybe remove once we get more info on this situation
  const longLoggingTimer = setTimeout(() => {
    logger.info({
      message: `${name} didn't init after ${LONG_LOGGER_MS}ms, app was already allowed to start. Failed maybe?`,
      duration: Date.now() - start,
      isOptionalAppStartLog: true,
    });
  }, LONG_LOGGER_MS);

  const initPromise = promise
    .catch(error => {
      logger.error({
        message: `${name} init failed`,
        error,
        duration: Date.now() - start,
        isOptionalAppStartLog: true,
      });
    })
    .finally(() => {
      clearTimeout(timeoutTimer);
      clearTimeout(longLoggingTimer);
    });

  await Promise.race([initPromise, loggingTimeoutPromise]);
}

let optionalAppStartPromises: Promise<void[]> | null = null;
const initializeOptionalAppStart = (): Promise<void[]> => {
  if (!optionalAppStartPromises) {
    optionalAppStartPromises = Promise.all([
      optionalAppStartPromise(initLaunchDarklyPromise, 'LaunchDarkly'),
      optionalAppStartPromise(initializeExperiments(), 'Experiments'),
    ]);
  }

  return optionalAppStartPromises;
};

const initializeAmplitudeSessionHeader = async (): Promise<void> => {
  await optionalAppStartPromise(initializeAmplitude(), 'Amplitude');

  if (!headers[Header.SESSION_ID]) {
    setHeaders(Header.SESSION_ID, Date.now().toString());
    logger.warn({
      message:
        'Amplitude session header was not ready before app start; using fallback session header.',
      isOptionalAppStartLog: true,
    });
  }
};

let appReadyPromiseResolved = false;
const requiredAppStartPromises = Promise.all([
  initExpoFonts(fonts),
  TEMP_asyncToSyncStorageMigration(),
] as Array<Promise<Asset | void>>);

type AppInnerProps = {
  routes: React.ReactNode;
  onReadyToHideSplash?: () => void;
};

export const AppInner: React.FC<AppInnerProps> = ({ routes, onReadyToHideSplash }) => {
  const [isAppReady, setIsAppReady] = useState(appReadyPromiseResolved);
  const [isAuthStorageReady, setIsAuthStorageReady] = useState(isSecureAuthStorageReady());
  const [isAuthBootstrapComplete, setIsAuthBootstrapComplete] = useState(appReadyPromiseResolved);
  const hasNotifiedReadyToHideSplash = useRef(false);
  const showEnvPickerAtStart = shouldShowEnvPickerAtStart();
  const isReadyToRenderEnvPicker =
    showEnvPickerAtStart && isAuthStorageReady && isAuthBootstrapComplete;
  const isReadyToRenderProviders =
    !showEnvPickerAtStart && isAuthStorageReady && isAuthBootstrapComplete && isAppReady;
  const isReadyToRenderApp = isReadyToRenderEnvPicker || isReadyToRenderProviders;

  useEffect(() => {
    const init = async () => {
      try {
        logger.info({ message: 'React app started', isAppStart: true });

        await requiredAppStartPromises;
        await initializeSecureAuthStorage();
        await TEMP_migrateAuthKeys();
        setIsAuthStorageReady(true);
        setIsAuthBootstrapComplete(true);

        if (showEnvPickerAtStart) {
          return;
        }

        await initializeAmplitudeSessionHeader();
        void initializeOptionalAppStart();

        // Wait for LocalStorage sync to complete before syncing Redux state
        store.dispatch(syncWithStorage());

        appReadyPromiseResolved = true;
        setIsAppReady(true);
        // eslint-disable-next-line prettier/prettier
      } catch (error) {
        handleFatalError('InitError', error);
      }
    };

    init();
  }, [showEnvPickerAtStart]);

  useEffect(() => {
    if (!isReadyToRenderApp) {
      return;
    }

    if (!hasNotifiedReadyToHideSplash.current) {
      hasNotifiedReadyToHideSplash.current = true;
      onReadyToHideSplash?.();
    }

    let isMounted = true;
    void animatedSplashScreenHiddenDeferred.promise.then(() => {
      if (!isMounted) {
        return;
      }

      markEasObserveInteractiveOnce(showEnvPickerAtStart ? { routeName: 'env-picker' } : undefined);
    });

    return () => {
      isMounted = false;
    };
  }, [isReadyToRenderApp, onReadyToHideSplash, showEnvPickerAtStart]);

  useOtaLogger();
  // This hook initializes the Branch deeplinks manager
  // and handles both live and deferred deep links.
  useDeeplinks({ isAppReady });
  useAppShortcuts({ isAppReady });

  // If env/region are provided in the URL, set them before rendering the overlay
  useApplyEnvRegionFromUrl();

  if (!isReadyToRenderApp) {
    return (
      <View style={styles.notReadyWrap}>
        <ActivityIndicator size="large" color={primitive.$fieryRed} />
      </View>
    );
  }

  if (isReadyToRenderEnvPicker) {
    return <EnvPicker />;
  }

  return (
    <ReduceProviders>
      <Wrapper>
        <PreloadMinimalFavorites />
        <DiagnosticContextGlobalContributors />
        {routes}
        <GlobalStylesheet />
        <DefaultHelmetHTML />
        <GlobalAppModals />
        <RootOnlyGlobalAppModals />
        <PlatformSpecificMiscellaneous />
        <GenericMiscellaneous />
        <DialogStaticPage />
      </Wrapper>
    </ReduceProviders>
  );
};

const styles = StyleSheet.create({
  notReadyWrap: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
  },
});
